import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { 
  Search, Filter, X, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown,
  Target, Link2, Download, Calendar, Plus, Trash2, Save, ChevronDown,
  SlidersHorizontal, Check, BookmarkPlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status badge styles
const statusStyles = {
  new: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  'new lead': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  lead: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  contacted: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  qualified: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  opportunity: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  customer: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  warm: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  cold: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  lost: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
};

// Available filter properties for advanced filtering
const FILTER_PROPERTIES = [
  // Contact Activity
  { id: 'created_at', label: 'Create Date', type: 'date', category: 'Contact activity' },
  { id: 'last_activity_at', label: 'Last Activity Date', type: 'date', category: 'Contact activity' },
  { id: 'assigned_at', label: 'Assigned Date', type: 'date', category: 'Contact activity' },
  { id: 'times_contacted', label: 'Times Contacted', type: 'number', category: 'Contact activity' },
  { id: 'sales_activities_count', label: 'Sales Activities Count', type: 'number', category: 'Contact activity' },
  
  // Contact Info
  { id: 'source', label: 'Lead Source', type: 'select', category: 'Contact information' },
  { id: 'status', label: 'Lead Status', type: 'select', category: 'Contact information' },
  { id: 'lifecycle_stage', label: 'Lifecycle Stage', type: 'select', category: 'Contact information' },
  { id: 'owner_id', label: 'Contact Owner', type: 'select', category: 'Contact information' },
  { id: 'email', label: 'Email', type: 'text', category: 'Contact information' },
  { id: 'phone', label: 'Phone Number', type: 'text', category: 'Contact information' },
  { id: 'company', label: 'Company', type: 'text', category: 'Contact information' },
  
  // UTM / Campaign
  { id: 'utm_source', label: 'UTM Source', type: 'text', category: 'Campaign / UTM' },
  { id: 'utm_medium', label: 'UTM Medium', type: 'text', category: 'Campaign / UTM' },
  { id: 'utm_campaign', label: 'UTM Campaign', type: 'text', category: 'Campaign / UTM' },
  { id: 'utm_content', label: 'UTM Content', type: 'text', category: 'Campaign / UTM' },
  { id: 'utm_term', label: 'UTM Term', type: 'text', category: 'Campaign / UTM' },
  
  // Qualification
  { id: 'lead_score', label: 'Lead Score', type: 'number', category: 'Qualification' },
  { id: 'priority', label: 'Priority', type: 'select', category: 'Qualification' },
];

// Operators by type
const OPERATORS = {
  text: [
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'equals', label: 'is equal to' },
    { value: 'not_equals', label: 'is not equal to' },
    { value: 'is_known', label: 'is known' },
    { value: 'is_unknown', label: 'is unknown' },
  ],
  select: [
    { value: 'is_any', label: 'is any of' },
    { value: 'is_none', label: 'is none of' },
    { value: 'is_known', label: 'is known' },
    { value: 'is_unknown', label: 'is unknown' },
  ],
  number: [
    { value: 'equals', label: 'is equal to' },
    { value: 'not_equals', label: 'is not equal to' },
    { value: 'gt', label: 'is greater than' },
    { value: 'gte', label: 'is greater than or equal to' },
    { value: 'lt', label: 'is less than' },
    { value: 'lte', label: 'is less than or equal to' },
    { value: 'is_known', label: 'is known' },
    { value: 'is_unknown', label: 'is unknown' },
  ],
  date: [
    { value: 'is', label: 'is' },
    { value: 'is_before', label: 'is before' },
    { value: 'is_after', label: 'is after' },
    { value: 'is_between', label: 'is between' },
    { value: 'is_known', label: 'is known' },
    { value: 'is_unknown', label: 'is unknown' },
  ],
};

const MarketingSales = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Data states
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ sources: [], statuses: [], owners: [], lifecycleStages: [] });
  
  // Tab state
  const [activeTab, setActiveTab] = useState('all');
  
  // Basic Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [utmSourceFilter, setUtmSourceFilter] = useState('');
  const [utmMediumFilter, setUtmMediumFilter] = useState('');
  const [utmCampaignFilter, setUtmCampaignFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [lastActivityFrom, setLastActivityFrom] = useState('');
  const [lastActivityTo, setLastActivityTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [quickDateFilter, setQuickDateFilter] = useState('all');
  
  // Advanced Filter states
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [filterLogic, setFilterLogic] = useState('AND'); // AND or OR
  const [showAddFilterPopover, setShowAddFilterPopover] = useState(false);
  const [filterSearchQuery, setFilterSearchQuery] = useState('');
  
  // Saved Views states
  const [savedViews, setSavedViews] = useState([]);
  const [showSaveViewDialog, setShowSaveViewDialog] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  
  // Quick date filter options
  const quickDateOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
  ];
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Multi-select states
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [exporting, setExporting] = useState(false);
  
  // Helper to format date as YYYY-MM-DD
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Helper to calculate date range based on quick filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case 'today':
        return { from: formatDateLocal(today), to: formatDateLocal(today) };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: formatDateLocal(yesterday), to: formatDateLocal(yesterday) };
      case 'this_week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return { from: formatDateLocal(startOfWeek), to: formatDateLocal(today) };
      case 'last_week':
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        return { from: formatDateLocal(lastWeekStart), to: formatDateLocal(lastWeekEnd) };
      case 'this_month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: formatDateLocal(startOfMonth), to: formatDateLocal(today) };
      case 'last_month':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        return { from: formatDateLocal(lastMonthStart), to: formatDateLocal(lastMonthEnd) };
      case 'this_year':
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        return { from: formatDateLocal(startOfYear), to: formatDateLocal(today) };
      default:
        return { from: '', to: '' };
    }
  };
  
  // Handle quick date filter change
  const handleQuickDateChange = (value) => {
    setQuickDateFilter(value);
    if (value !== 'custom' && value !== 'all') {
      const range = getDateRange(value);
      setDateFrom(range.from);
      setDateTo(range.to);
    } else if (value === 'all') {
      setDateFrom('');
      setDateTo('');
    }
  };

  // Check for active filters
  const hasActiveFilters = sourceFilter !== 'all' || statusFilter !== 'all' || 
    lifecycleFilter !== 'all' || ownerFilter !== 'all' ||
    utmSourceFilter || utmMediumFilter || utmCampaignFilter ||
    dateFrom || dateTo || lastActivityFrom || lastActivityTo || 
    searchQuery || advancedFilters.length > 0;

  // Get active filter count
  const activeFilterCount = [
    sourceFilter !== 'all',
    statusFilter !== 'all',
    lifecycleFilter !== 'all',
    ownerFilter !== 'all',
    !!utmSourceFilter,
    !!utmMediumFilter,
    !!utmCampaignFilter,
    !!dateFrom || !!dateTo,
    !!lastActivityFrom || !!lastActivityTo,
    ...advancedFilters.map(() => true)
  ].filter(Boolean).length;

  // Advanced filter handlers
  const addAdvancedFilter = (property) => {
    const prop = FILTER_PROPERTIES.find(p => p.id === property);
    if (!prop) return;
    
    const defaultOperator = OPERATORS[prop.type]?.[0]?.value || 'equals';
    setAdvancedFilters([
      ...advancedFilters,
      {
        id: Date.now(),
        property: property,
        operator: defaultOperator,
        value: '',
        value2: '' // For "between" operator
      }
    ]);
    setShowAddFilterPopover(false);
    setFilterSearchQuery('');
  };

  const updateAdvancedFilter = (filterId, field, value) => {
    setAdvancedFilters(advancedFilters.map(f => 
      f.id === filterId ? { ...f, [field]: value } : f
    ));
  };

  const removeAdvancedFilter = (filterId) => {
    setAdvancedFilters(advancedFilters.filter(f => f.id !== filterId));
  };

  // Get filter options for a property
  const getPropertyOptions = (propertyId) => {
    switch (propertyId) {
      case 'source':
        return filterOptions.sources.map(s => ({ value: s, label: s }));
      case 'status':
        return filterOptions.statuses.map(s => ({ value: s, label: s }));
      case 'lifecycle_stage':
        return filterOptions.lifecycleStages?.map(s => ({ value: s, label: s })) || [];
      case 'owner_id':
        return filterOptions.owners.map(o => ({ value: o.id, label: o.name }));
      case 'priority':
        return [
          { value: 'high', label: 'High' },
          { value: 'medium', label: 'Medium' },
          { value: 'normal', label: 'Normal' },
          { value: 'low', label: 'Low' },
        ];
      default:
        return [];
    }
  };

  // Filter properties by search
  const filteredProperties = FILTER_PROPERTIES.filter(prop =>
    prop.label.toLowerCase().includes(filterSearchQuery.toLowerCase()) ||
    prop.category.toLowerCase().includes(filterSearchQuery.toLowerCase())
  );

  // Group properties by category
  const groupedProperties = filteredProperties.reduce((acc, prop) => {
    if (!acc[prop.category]) acc[prop.category] = [];
    acc[prop.category].push(prop);
    return acc;
  }, {});

  // Save current view
  const saveCurrentView = () => {
    if (!newViewName.trim()) {
      toast.error('Please enter a view name');
      return;
    }
    
    const view = {
      id: Date.now(),
      name: newViewName.trim(),
      filters: {
        sourceFilter,
        statusFilter,
        lifecycleFilter,
        ownerFilter,
        utmSourceFilter,
        utmMediumFilter,
        utmCampaignFilter,
        dateFrom,
        dateTo,
        lastActivityFrom,
        lastActivityTo,
        advancedFilters,
        filterLogic,
        activeTab
      },
      createdAt: new Date().toISOString()
    };
    
    const updatedViews = [...savedViews, view];
    setSavedViews(updatedViews);
    localStorage.setItem('marketingSalesViews', JSON.stringify(updatedViews));
    setNewViewName('');
    setShowSaveViewDialog(false);
    toast.success(`View "${view.name}" saved`);
  };

  // Load saved view
  const loadSavedView = (view) => {
    const f = view.filters;
    setSourceFilter(f.sourceFilter || 'all');
    setStatusFilter(f.statusFilter || 'all');
    setLifecycleFilter(f.lifecycleFilter || 'all');
    setOwnerFilter(f.ownerFilter || 'all');
    setUtmSourceFilter(f.utmSourceFilter || '');
    setUtmMediumFilter(f.utmMediumFilter || '');
    setUtmCampaignFilter(f.utmCampaignFilter || '');
    setDateFrom(f.dateFrom || '');
    setDateTo(f.dateTo || '');
    setLastActivityFrom(f.lastActivityFrom || '');
    setLastActivityTo(f.lastActivityTo || '');
    setAdvancedFilters(f.advancedFilters || []);
    setFilterLogic(f.filterLogic || 'AND');
    if (f.activeTab) setActiveTab(f.activeTab);
    toast.success(`Loaded view "${view.name}"`);
  };

  // Delete saved view
  const deleteSavedView = (viewId) => {
    const updatedViews = savedViews.filter(v => v.id !== viewId);
    setSavedViews(updatedViews);
    localStorage.setItem('marketingSalesViews', JSON.stringify(updatedViews));
    toast.success('View deleted');
  };

  // Load saved views on mount
  useEffect(() => {
    const stored = localStorage.getItem('marketingSalesViews');
    if (stored) {
      try {
        setSavedViews(JSON.parse(stored));
      } catch (e) {
        console.error('Error loading saved views:', e);
      }
    }
  }, []);

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        token,
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (searchQuery) params.append('search', searchQuery);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (lifecycleFilter !== 'all') params.append('lifecycle_stage', lifecycleFilter);
      if (ownerFilter !== 'all') params.append('owner_id', ownerFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (lastActivityFrom) params.append('last_activity_from', lastActivityFrom);
      if (lastActivityTo) params.append('last_activity_to', lastActivityTo);
      
      // UTM filters - only apply when on UTM Tracker tab
      if (activeTab === 'utm_tracker') {
        params.append('has_utm', 'true');
        if (utmSourceFilter) params.append('utm_source', utmSourceFilter);
        if (utmMediumFilter) params.append('utm_medium', utmMediumFilter);
        if (utmCampaignFilter) params.append('utm_campaign', utmCampaignFilter);
      }
      
      // Advanced filters - serialize and send
      if (advancedFilters.length > 0) {
        params.append('advanced_filters', JSON.stringify(advancedFilters));
        params.append('filter_logic', filterLogic);
      }

      const response = await axios.get(`${API}/contacts/utm-tracker?${params}`);

      if (response.data.success) {
        setContacts(response.data.contacts || []);
        setTotalContacts(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / pageSize));
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/contacts/filters?token=${token}`);
      setFilterOptions({
        ...response.data,
        lifecycleStages: response.data.lifecycle_stages || response.data.lifecycleStages || []
      });
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchFilterOptions();
  }, [token, currentPage, pageSize, sortBy, sortOrder, activeTab]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setCurrentPage(1);
      fetchContacts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, sourceFilter, statusFilter, lifecycleFilter, ownerFilter, utmSourceFilter, utmMediumFilter, utmCampaignFilter, dateFrom, dateTo, lastActivityFrom, lastActivityTo, advancedFilters, filterLogic]);

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setSourceFilter('all');
    setStatusFilter('all');
    setLifecycleFilter('all');
    setOwnerFilter('all');
    setUtmSourceFilter('');
    setUtmMediumFilter('');
    setUtmCampaignFilter('');
    setDateFrom('');
    setDateTo('');
    setLastActivityFrom('');
    setLastActivityTo('');
    setQuickDateFilter('all');
    setAdvancedFilters([]);
    setCurrentPage(1);
  };

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'numeric', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  // Split name into first and last
  const splitName = (fullName) => {
    if (!fullName) return { firstName: '-', lastName: '' };
    const parts = fullName.trim().split(' ');
    const firstName = parts[0] || '-';
    const lastName = parts.slice(1).join(' ') || '';
    return { firstName, lastName };
  };

  // Export to CSV function
  const handleExport = async () => {
    try {
      setExporting(true);
      toast.info('Preparing export...');
      
      const params = new URLSearchParams({
        token,
        page: '1',
        limit: '10000',
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (searchQuery) params.append('search', searchQuery);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      if (activeTab === 'utm_tracker') {
        params.append('has_utm', 'true');
        if (utmSourceFilter) params.append('utm_source', utmSourceFilter);
        if (utmMediumFilter) params.append('utm_medium', utmMediumFilter);
        if (utmCampaignFilter) params.append('utm_campaign', utmCampaignFilter);
      }

      const response = await axios.get(`${API}/contacts/utm-tracker?${params}`);

      if (response.data.success && response.data.contacts) {
        const exportData = response.data.contacts;
        
        let headers, rows;
        
        if (activeTab === 'all') {
          // All Leads - simplified columns
          headers = ['Record ID', 'Name', 'Email', 'Created Date', 'Lead Status', 'Lead Source'];
          rows = exportData.map(contact => [
            contact.id || '',
            contact.name || '',
            contact.email || '',
            formatDateDisplay(contact.created_at),
            contact.lead_status || contact.status || 'New Lead',
            contact.source || ''
          ]);
        } else {
          // UTM Tracker - detailed columns
          headers = [
            'Record ID', 'First Name', 'Last Name', 'Phone Number', 'Create Date',
            'Last Activity Date', 'Email', 'Lead Status', 'Lifecycle Stage',
            'Lead Source', 'Contact Owner', '# Sales Activities', '# Times Contacted'
          ];
          rows = exportData.map(contact => {
            const { firstName, lastName } = splitName(contact.name);
            return [
              contact.id || '',
              firstName,
              lastName || '',
              contact.phone || '',
              formatDateDisplay(contact.created_at),
              formatDateDisplay(contact.last_activity_at || contact.updated_at),
              contact.email || '',
              contact.lead_status || contact.status || 'New Lead',
              contact.lifecycle_stage || contact.status || '',
              contact.source || '',
              contact.owner_name || contact.assigned_to_name || '',
              contact.sales_activities_count || 0,
              contact.times_contacted || 0
            ];
          });
        }
        
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => {
            const cellStr = String(cell);
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `marketing_sales_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Exported ${exportData.length} leads successfully`);
      } else {
        toast.error('No data to export');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Sort handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Selection handlers
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  // Handle contact view
  const handleViewContact = async (contact) => {
    navigate(`/contacts/${contact.id}`);
  };

  const getStatusStyle = (status) => {
    const normalizedStatus = status?.toLowerCase() || 'new';
    return statusStyles[normalizedStatus] || statusStyles.new;
  };

  // Render Advanced Filter Row
  const renderAdvancedFilterRow = (filter, index) => {
    const property = FILTER_PROPERTIES.find(p => p.id === filter.property);
    if (!property) return null;
    
    const operators = OPERATORS[property.type] || [];
    const needsValue = !['is_known', 'is_unknown'].includes(filter.operator);
    const needsSecondValue = filter.operator === 'is_between';
    const options = getPropertyOptions(filter.property);
    
    return (
      <div key={filter.id} className="flex items-center gap-2 py-2 px-3 bg-slate-50 rounded-lg border border-slate-200">
        {index > 0 && (
          <span className="text-xs font-medium text-slate-500 w-10">{filterLogic}</span>
        )}
        
        {/* Property Name */}
        <div className="min-w-[120px]">
          <span className="text-sm font-medium text-slate-700">{property.label}</span>
        </div>
        
        {/* Operator */}
        <Select 
          value={filter.operator} 
          onValueChange={(v) => updateAdvancedFilter(filter.id, 'operator', v)}
        >
          <SelectTrigger className="w-[160px] h-8 bg-white text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {operators.map(op => (
              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Value Input */}
        {needsValue && property.type === 'select' && (
          <Select 
            value={filter.value} 
            onValueChange={(v) => updateAdvancedFilter(filter.id, 'value', v)}
          >
            <SelectTrigger className="w-[160px] h-8 bg-white text-sm">
              <SelectValue placeholder="Select value..." />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        {needsValue && property.type === 'text' && (
          <Input
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
            placeholder="Enter value..."
            className="w-[160px] h-8 bg-white text-sm"
          />
        )}
        
        {needsValue && property.type === 'number' && (
          <Input
            type="number"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
            placeholder="Enter number..."
            className="w-[100px] h-8 bg-white text-sm"
          />
        )}
        
        {needsValue && property.type === 'date' && (
          <>
            <Input
              type="date"
              value={filter.value}
              onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
              className="w-[140px] h-8 bg-white text-sm"
            />
            {needsSecondValue && (
              <>
                <span className="text-slate-400 text-xs">and</span>
                <Input
                  type="date"
                  value={filter.value2}
                  onChange={(e) => updateAdvancedFilter(filter.id, 'value2', e.target.value)}
                  className="w-[140px] h-8 bg-white text-sm"
                />
              </>
            )}
          </>
        )}
        
        {/* Remove Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => removeAdvancedFilter(filter.id)}
          className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50"
        >
          <X size={14} />
        </Button>
      </div>
    );
  };

  // Loading state
  if (loading && contacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="marketing-sales-loading">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="animate-spin text-slate-400" size={32} />
          <span className="text-slate-500 text-sm">Loading marketing leads...</span>
        </div>
      </div>
    );
  }

  // Render All Leads Table
  const renderAllLeadsTable = () => (
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50 border-b border-slate-200">
          <TableHead className="w-10 py-3 px-4">
            <Checkbox
              checked={selectedContacts.length === contacts.length && contacts.length > 0}
              onCheckedChange={toggleSelectAll}
            />
          </TableHead>
          <TableHead className="py-3 px-4 text-xs font-bold text-slate-700 uppercase tracking-wider">
            Record ID
          </TableHead>
          <TableHead 
            className="py-3 px-4 text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:text-slate-900"
            onClick={() => handleSort('name')}
          >
            <div className="flex items-center gap-1">
              Name
              {sortBy === 'name' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
            </div>
          </TableHead>
          <TableHead className="py-3 px-4 text-xs font-bold text-slate-700 uppercase tracking-wider">
            Email
          </TableHead>
          <TableHead 
            className="py-3 px-4 text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:text-slate-900"
            onClick={() => handleSort('created_at')}
          >
            <div className="flex items-center gap-1">
              Created Date
              {sortBy === 'created_at' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
            </div>
          </TableHead>
          <TableHead className="py-3 px-4 text-xs font-bold text-slate-700 uppercase tracking-wider">
            Lead Status
          </TableHead>
          <TableHead className="py-3 px-4 text-xs font-bold text-slate-700 uppercase tracking-wider">
            Lead Source
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contacts.map((contact) => {
          const isSelected = selectedContacts.includes(contact.id);
          const statusStyle = getStatusStyle(contact.lead_status || contact.status);

          return (
            <TableRow 
              key={contact.id}
              className={`cursor-pointer border-b border-slate-100 transition-colors ${isSelected ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
              onClick={() => handleViewContact(contact)}
            >
              <TableCell className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleContactSelection(contact.id)}
                />
              </TableCell>
              <TableCell className="py-3 px-4">
                <span className="text-xs text-slate-500 font-mono">{contact.id?.slice(0, 8) || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-4">
                <span className="text-sm font-medium text-slate-900">{contact.name || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-4">
                <span className="text-sm text-slate-600">{contact.email || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-4">
                <span className="text-xs text-slate-500">{formatDateDisplay(contact.created_at)}</span>
              </TableCell>
              <TableCell className="py-3 px-4">
                <Badge className={`${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border} text-xs font-medium rounded-md`}>
                  {contact.lead_status || contact.status || 'New Lead'}
                </Badge>
              </TableCell>
              <TableCell className="py-3 px-4">
                <span className="text-sm text-slate-600">{contact.source || '-'}</span>
              </TableCell>
            </TableRow>
          );
        })}
        {contacts.length === 0 && !loading && (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                  <Link2 size={24} className="text-violet-400" />
                </div>
                <div>
                  <p className="text-slate-600 font-medium">No leads found</p>
                  <p className="text-sm text-slate-400">
                    {hasActiveFilters ? 'Try adjusting your filters' : 'No marketing leads yet'}
                  </p>
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  // Render UTM Tracker Table
  const renderUtmTrackerTable = () => (
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50 border-b border-slate-200">
          <TableHead className="w-10 py-3 px-3">
            <Checkbox
              checked={selectedContacts.length === contacts.length && contacts.length > 0}
              onCheckedChange={toggleSelectAll}
            />
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
            Record ID
          </TableHead>
          <TableHead 
            className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:text-slate-900 whitespace-nowrap"
            onClick={() => handleSort('name')}
          >
            <div className="flex items-center gap-1">
              First Name
              {sortBy === 'name' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
            </div>
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
            Last Name
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
            Phone Number
          </TableHead>
          <TableHead 
            className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:text-slate-900 whitespace-nowrap"
            onClick={() => handleSort('created_at')}
          >
            <div className="flex items-center gap-1">
              Create Date
              {sortBy === 'created_at' ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
            </div>
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
            Last Activity
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
            Email
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
            Lead Status
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
            Lifecycle Stage
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
            Lead Source
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
            Contact Owner
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap text-center">
            # Sales Activities
          </TableHead>
          <TableHead className="py-3 px-3 text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap text-center">
            # Times Contacted
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contacts.map((contact) => {
          const isSelected = selectedContacts.includes(contact.id);
          const { firstName, lastName } = splitName(contact.name);
          const statusStyle = getStatusStyle(contact.lead_status || contact.status);

          return (
            <TableRow 
              key={contact.id}
              className={`cursor-pointer border-b border-slate-100 transition-colors ${isSelected ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
              onClick={() => handleViewContact(contact)}
            >
              <TableCell className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleContactSelection(contact.id)}
                />
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-xs text-slate-500 font-mono">{contact.id?.slice(0, 8) || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-sm font-medium text-slate-900">{firstName}</span>
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-sm text-slate-600">{lastName || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-sm text-slate-600 whitespace-nowrap">{contact.phone || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-xs text-slate-500 whitespace-nowrap">{formatDateDisplay(contact.created_at)}</span>
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-xs text-slate-500 whitespace-nowrap">{formatDateDisplay(contact.last_activity_at || contact.updated_at)}</span>
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-sm text-slate-600 truncate block max-w-[160px]" title={contact.email}>{contact.email || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-3">
                <Badge className={`${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border} text-xs font-medium rounded-md whitespace-nowrap`}>
                  {contact.lead_status || contact.status || 'New Lead'}
                </Badge>
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-sm text-slate-600 whitespace-nowrap">{contact.lifecycle_stage || contact.status || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-sm text-slate-600 whitespace-nowrap">{contact.source || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-3">
                <span className="text-sm text-slate-600 truncate block max-w-[100px]" title={contact.owner_name}>{contact.owner_name || contact.assigned_to_name || '-'}</span>
              </TableCell>
              <TableCell className="py-3 px-3 text-center">
                <span className="text-sm font-medium text-slate-700">{contact.sales_activities_count || 0}</span>
              </TableCell>
              <TableCell className="py-3 px-3 text-center">
                <span className="text-sm font-medium text-slate-700">{contact.times_contacted || 0}</span>
              </TableCell>
            </TableRow>
          );
        })}
        {contacts.length === 0 && !loading && (
          <TableRow>
            <TableCell colSpan={14} className="text-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                  <Target size={24} className="text-violet-400" />
                </div>
                <div>
                  <p className="text-slate-600 font-medium">No leads found</p>
                  <p className="text-sm text-slate-400">
                    {hasActiveFilters ? 'Try adjusting your filters' : 'No leads with UTM data yet'}
                  </p>
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <div data-testid="marketing-sales-page" className="space-y-4 p-6 md:p-8 max-w-[1800px] mx-auto">
      {/* Header Section */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <Target className="h-6 w-6 text-violet-600" />
            Marketing Sales - UTM Tracker
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalContacts.toLocaleString()} leads
            {activeTab === 'utm_tracker' && ' with UTM data'}
            {hasActiveFilters && <span className="text-slate-400"> (filtered)</span>}
          </p>
        </div>
        
        {/* Date Filter + Export + Refresh - Right side */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <Select value={quickDateFilter} onValueChange={handleQuickDateChange}>
              <SelectTrigger className="w-[130px] h-9 bg-white border-slate-200 text-sm" data-testid="quick-date-filter">
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                {quickDateOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {quickDateFilter === 'custom' && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-[120px] bg-white text-sm"
              />
              <span className="text-slate-400">-</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 w-[120px] bg-white text-sm"
              />
            </div>
          )}
          
          <Button 
            variant="outline"
            onClick={handleExport}
            disabled={exporting || totalContacts === 0}
            className="gap-2 h-9"
            data-testid="export-btn"
          >
            <Download size={16} className={exporting ? 'animate-pulse' : ''} />
            Export
          </Button>
          
          <Button 
            variant="outline"
            onClick={() => fetchContacts()}
            className="gap-2 h-9"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setCurrentPage(1); setSelectedContacts([]); }}>
        <TabsList className="bg-transparent border-b border-slate-200 w-full justify-start rounded-none h-auto p-0 gap-0">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:border-b-2 data-[state=active]:border-violet-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none px-4 py-3 text-sm font-medium text-slate-500 data-[state=active]:text-violet-600 hover:text-slate-700 transition-colors"
            data-testid="tab-all"
          >
            <Link2 className="h-4 w-4 mr-2" />
            All Leads
          </TabsTrigger>
          <TabsTrigger 
            value="utm_tracker" 
            className="data-[state=active]:border-b-2 data-[state=active]:border-violet-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none px-4 py-3 text-sm font-medium text-slate-500 data-[state=active]:text-violet-600 hover:text-slate-700 transition-colors"
            data-testid="tab-utm-tracker"
          >
            <Target className="h-4 w-4 mr-2" />
            UTM Tracker
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Basic Filters Section */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
        {/* Row 1: Search + Quick Actions */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search leads by name, email, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 bg-white border-slate-200 rounded-lg"
              data-testid="search-input"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Advanced Filters Button */}
            <Button
              variant={advancedFilters.length > 0 ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAdvancedFilters(true)}
              className={`h-9 gap-1.5 ${advancedFilters.length > 0 ? 'bg-violet-600 text-white hover:bg-violet-700' : 'border-slate-200'}`}
              data-testid="advanced-filters-btn"
            >
              <SlidersHorizontal size={14} />
              Advanced Filters
              {advancedFilters.length > 0 && (
                <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center bg-white text-violet-600 text-xs">
                  {advancedFilters.length}
                </Badge>
              )}
            </Button>

            {/* Saved Views Dropdown */}
            {savedViews.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 gap-1.5 border-slate-200">
                    <BookmarkPlus size={14} />
                    Saved Views
                    <ChevronDown size={12} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="end">
                  <div className="space-y-1">
                    {savedViews.map(view => (
                      <div 
                        key={view.id} 
                        className="flex items-center justify-between p-2 rounded hover:bg-slate-50 cursor-pointer group"
                        onClick={() => loadSavedView(view)}
                      >
                        <span className="text-sm text-slate-700">{view.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteSavedView(view.id); }}
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters} 
                className="h-9 text-slate-500 hover:text-slate-700"
                data-testid="clear-filters-btn"
              >
                <X size={14} className="mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Row 2: Basic Filter Dropdowns */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Lead Source */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Source:</Label>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[140px] h-8 bg-white border-slate-200 text-sm" data-testid="source-filter">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {filterOptions.sources.map(source => (
                  <SelectItem key={source} value={source}>{source}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lead Status */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Status:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] h-8 bg-white border-slate-200 text-sm" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {filterOptions.statuses.map(status => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lifecycle Stage */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Stage:</Label>
            <Select value={lifecycleFilter} onValueChange={setLifecycleFilter}>
              <SelectTrigger className="w-[140px] h-8 bg-white border-slate-200 text-sm" data-testid="lifecycle-filter">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {filterOptions.lifecycleStages?.map(stage => (
                  <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contact Owner */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Owner:</Label>
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[150px] h-8 bg-white border-slate-200 text-sm" data-testid="owner-filter">
                <SelectValue placeholder="All Owners" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Owners</SelectItem>
                {filterOptions.owners?.map(owner => (
                  <SelectItem key={owner.id} value={owner.id}>{owner.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Range Filters */}
          <div className="flex items-center gap-2 border-l border-slate-200 pl-3 ml-1">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Created:</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setQuickDateFilter('custom'); }}
              className="h-8 w-[120px] bg-white text-xs"
              placeholder="From"
            />
            <span className="text-slate-300">-</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setQuickDateFilter('custom'); }}
              className="h-8 w-[120px] bg-white text-xs"
              placeholder="To"
            />
          </div>

          {/* Last Activity Date */}
          <div className="flex items-center gap-2 border-l border-slate-200 pl-3 ml-1">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Last Activity:</Label>
            <Input
              type="date"
              value={lastActivityFrom}
              onChange={(e) => setLastActivityFrom(e.target.value)}
              className="h-8 w-[120px] bg-white text-xs"
              placeholder="From"
            />
            <span className="text-slate-300">-</span>
            <Input
              type="date"
              value={lastActivityTo}
              onChange={(e) => setLastActivityTo(e.target.value)}
              className="h-8 w-[120px] bg-white text-xs"
              placeholder="To"
            />
          </div>
        </div>

        {/* Row 3: Active Advanced Filters Display (if any) */}
        {advancedFilters.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-slate-500">Advanced Filters:</span>
              <Select value={filterLogic} onValueChange={setFilterLogic}>
                <SelectTrigger className="w-[70px] h-6 bg-slate-100 border-0 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">AND</SelectItem>
                  <SelectItem value="OR">OR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              {advancedFilters.map((filter, index) => renderAdvancedFilterRow(filter, index))}
            </div>
          </div>
        )}
      </div>

      {/* UTM Filters Row - Only for UTM Tracker tab */}
      {activeTab === 'utm_tracker' && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target size={16} className="text-violet-600" />
            <span className="text-sm font-medium text-violet-700">UTM / Campaign Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-violet-600 mb-1.5 block">UTM Source</Label>
              <Input
                placeholder="e.g., google, facebook"
                value={utmSourceFilter}
                onChange={(e) => setUtmSourceFilter(e.target.value)}
                className="h-9 bg-white border-violet-200"
              />
            </div>
            <div>
              <Label className="text-xs text-violet-600 mb-1.5 block">UTM Medium</Label>
              <Input
                placeholder="e.g., cpc, email"
                value={utmMediumFilter}
                onChange={(e) => setUtmMediumFilter(e.target.value)}
                className="h-9 bg-white border-violet-200"
              />
            </div>
            <div>
              <Label className="text-xs text-violet-600 mb-1.5 block">UTM Campaign</Label>
              <Input
                placeholder="e.g., summer_sale"
                value={utmCampaignFilter}
                onChange={(e) => setUtmCampaignFilter(e.target.value)}
                className="h-9 bg-white border-violet-200"
              />
            </div>
          </div>
        </div>
      )}

      {/* Advanced Filters Modal */}
      <Dialog open={showAdvancedFilters} onOpenChange={setShowAdvancedFilters}>
        <DialogContent className="sm:max-w-[500px] p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b border-slate-200">
            <DialogTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
              <SlidersHorizontal size={18} className="text-slate-600" />
              All Filters
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            {/* Active Filters Section */}
            <div className="px-6 py-5 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Active Filters</h3>
              {advancedFilters.length === 0 ? (
                <p className="text-sm text-slate-400">No advanced filters applied yet.</p>
              ) : (
                <div className="space-y-2 max-h-[180px] overflow-y-auto">
                  {/* Filter Logic Toggle - only show when multiple filters */}
                  {advancedFilters.length > 1 && (
                    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-100">
                      <span className="text-xs text-slate-500">Match:</span>
                      <div className="flex rounded-md border border-slate-200 overflow-hidden">
                        <button
                          onClick={() => setFilterLogic('AND')}
                          className={`px-3 py-1 text-xs font-medium transition-colors ${
                            filterLogic === 'AND' 
                              ? 'bg-violet-600 text-white' 
                              : 'bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          AND
                        </button>
                        <button
                          onClick={() => setFilterLogic('OR')}
                          className={`px-3 py-1 text-xs font-medium transition-colors ${
                            filterLogic === 'OR' 
                              ? 'bg-violet-600 text-white' 
                              : 'bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          OR
                        </button>
                      </div>
                    </div>
                  )}
                  {advancedFilters.map((filter, index) => renderAdvancedFilterRow(filter, index))}
                </div>
              )}
            </div>

            {/* Add Filter Section */}
            <div className="px-6 py-5">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">Add Filter</h3>
                <Popover open={showAddFilterPopover} onOpenChange={setShowAddFilterPopover}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 gap-1.5 text-sm">
                      <Plus size={14} />
                      Add filter
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0" align="end" sideOffset={5}>
                    <div className="p-3 border-b border-slate-100">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Search in Contact properties"
                          value={filterSearchQuery}
                          onChange={(e) => setFilterSearchQuery(e.target.value)}
                          className="pl-8 h-8 text-sm bg-slate-50 border-slate-200"
                          autoFocus
                        />
                      </div>
                    </div>
                    <ScrollArea className="h-[280px]">
                      <div className="py-2">
                        {Object.entries(groupedProperties).map(([category, props]) => (
                          <div key={category} className="mb-2">
                            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 py-2 bg-slate-50">
                              {category}
                            </h4>
                            {props.map(prop => {
                              const isActive = advancedFilters.some(f => f.property === prop.id);
                              return (
                                <button
                                  key={prop.id}
                                  onClick={() => addAdvancedFilter(prop.id)}
                                  disabled={isActive}
                                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-slate-50 text-left transition-colors ${
                                    isActive ? 'opacity-50 cursor-not-allowed bg-slate-50' : ''
                                  }`}
                                >
                                  <span className="text-xs text-slate-400 w-6 text-center font-mono">
                                    {prop.type === 'text' && 'Abc'}
                                    {prop.type === 'number' && '#'}
                                    {prop.type === 'date' && '📅'}
                                    {prop.type === 'select' && '▼'}
                                  </span>
                                  <span className="text-slate-700 flex-1">{prop.label}</span>
                                  {isActive && <Check size={14} className="text-green-500" />}
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSaveViewDialog(true)}
              disabled={!hasActiveFilters}
              className="h-9 gap-1.5 text-slate-600 hover:text-slate-800"
            >
              <Save size={14} />
              Save View
            </Button>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowAdvancedFilters(false)}
                className="h-9 text-slate-600"
              >
                Cancel
              </Button>
              <Button 
                size="sm"
                onClick={() => setShowAdvancedFilters(false)} 
                className="h-9 bg-violet-600 hover:bg-violet-700 text-white"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save View Dialog */}
      <Dialog open={showSaveViewDialog} onOpenChange={setShowSaveViewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save Filter View</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-slate-700 mb-1.5 block">View Name</Label>
              <Input
                placeholder="e.g., Meta Leads - Last 7 Days"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                className="h-10"
                autoFocus
              />
            </div>
            <div className="text-xs text-slate-500">
              This will save your current filters including: {activeFilterCount} active filter(s)
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowSaveViewDialog(false)}>Cancel</Button>
            <Button onClick={saveCurrentView} className="bg-violet-600 hover:bg-violet-700">
              Save View
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="leads-table">
        <div className="overflow-x-auto">
          {activeTab === 'all' ? renderAllLeadsTable() : renderUtmTrackerTable()}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span>{((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalContacts)} of {totalContacts.toLocaleString()}</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                <SelectTrigger className="w-[70px] h-8 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span>per page</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="h-8 bg-white"
              >
                Previous
              </Button>
              <div className="px-3 text-sm text-slate-600">Page {currentPage} of {totalPages}</div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="h-8 bg-white"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketingSales;
